import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Loader2, Settings as SettingsIcon, Bell, Mail, Filter, ChevronDown, ChevronUp,
  BarChart3, Activity, Zap, Archive, Layers, Info, Wand2, Lock, ShieldAlert,
  AlertOctagon, Save, Bookmark, TrendingUp,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import MetricHelp from "@/components/MetricHelp";

// ---------------- Safety-level tone map --------------------------------
const SAFETY_TONE = {
  safe_auto:         { bg: "bg-[#DCFCE7]", text: "text-[#166534]", label: "Safe auto-fix", Icon: Wand2 },
  approval_required: { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Approval required", Icon: ShieldAlert },
  manual_only:       { bg: "bg-[#F1F5F9]", text: "text-[#334155]", label: "Manual only", Icon: Lock },
};

// ---------------- Severity tone map ------------------------------------
const SEVERITY_TONE = {
  critical: { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", border: "border-[#FCA5A5]", label: "Critical" },
  high:     { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", border: "border-[#FCA5A5]", label: "High" },
  medium:   { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", border: "border-[#FDE68A]", label: "Medium" },
  low:      { bg: "bg-[#E0E7FF]", text: "text-[#1E40AF]", border: "border-[#C7D2FE]", label: "Low" },
  info:     { bg: "bg-[#F1F5F9]", text: "text-[#334155]", border: "border-[#E2E8F0]", label: "Info" },
};

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const CAT_ICON = {
  coverage: Layers,
  job_health: Activity,
  legal_hold: ShieldCheck,
  audit: BarChart3,
  backup: Archive,
  exceptions: AlertTriangle,
  predictive: Zap,
};

// ---------------- Score dial ------------------------------------------
function ScoreDial({ score, grade }) {
  const value = Math.max(0, Math.min(100, Number(score) || 0));
  const color =
    value >= 90 ? "#166534" :
    value >= 80 ? "#1E3A8A" :
    value >= 70 ? "#92400E" :
    "#991B1B";
  const bg = `conic-gradient(${color} ${value * 3.6}deg, #E5E5E0 0deg)`;
  return (
    <div className="flex items-center gap-6" data-testid="compliance-score-dial">
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{ width: 168, height: 168, background: bg }}
      >
        <div
          className="absolute rounded-full bg-white flex flex-col items-center justify-center"
          style={{ width: 144, height: 144 }}
        >
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#1E3A8A] flex items-center gap-0.5">
            Overall Score
            <MetricHelp topicKey="compliance_score" size="sm" side="top" align="center" />
          </div>
          <div className="text-5xl font-semibold" style={{ color }}>
            {value.toFixed(1)}
          </div>
          <div className="text-sm text-slate-600 mt-0.5 flex items-center justify-center gap-0.5">
            Grade {grade || "—"}
            <MetricHelp topicKey="compliance_grade" size="sm" side="bottom" align="center" />
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-[#1E3A8A]">
          HarborWell Connect · Compliance Center
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-1">
          Automated Compliance Optimization
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-xl">
          Continuous self-audit across coverage, job health, legal holds,
          audits, backups, exceptions, and predictive risk. The nightly
          scan runs at 04:30 UTC — kick off an on-demand run anytime.
        </p>
      </div>
    </div>
  );
}

// ---------------- Category card ---------------------------------------
function CategoryCard({ cat, onClick, isSelected }) {
  const score = Number(cat.score ?? 100);
  const color =
    score >= 90 ? "#166534" :
    score >= 80 ? "#1E3A8A" :
    score >= 70 ? "#92400E" :
    "#991B1B";
  const Icon = CAT_ICON[cat.key] || Info;
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKey}
      className={`text-left p-4 rounded-2xl border transition-all bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#7E937D]/50 ${
        isSelected
          ? "border-[#1E3A8A] shadow-md"
          : "border-[#E5E5E0] hover:border-[#94A3B8]"
      }`}
      data-testid={`compliance-category-${cat.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#1E3A8A]" />
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Weight {cat.weight}
            </div>
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{cat.label}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold" style={{ color }}>
            {cat.active ? score.toFixed(0) : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
            {cat.active ? "Score" : "Pending"}
          </div>
        </div>
      </div>
      <div className="mt-3 w-full h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
        {cat.active ? (
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${score}%`, background: color }}
          />
        ) : (
          <div className="h-full rounded-full bg-[#E5E5E0]" style={{ width: "100%" }} />
        )}
      </div>
      <div className="mt-2 text-xs text-slate-600 flex items-center justify-between">
        <span>{cat.issue_count} issue{cat.issue_count === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1">
          {cat.units > 0 ? <span className="text-slate-400">{cat.units} checks</span> : null}
          <span onClick={(e) => e.stopPropagation()}>
            <MetricHelp
              topicKey={`category_${cat.key}`}
              size="sm"
              side="bottom"
              align="end"
            />
          </span>
        </span>
      </div>
    </div>
  );
}

// ---------------- Issues table ----------------------------------------
function IssuesTable({
  issues,
  categoryFilter,
  severityFilter,
  onSeverityFilterChange,
  selected,
  onSelectionChange,
  onRunFix,
  onConvertToException,
  scanId,
}) {
  const [expanded, setExpanded] = useState({});

  const filtered = useMemo(() => {
    let out = [...(issues || [])];
    if (categoryFilter) out = out.filter((i) => i.category === categoryFilter);
    if (severityFilter) out = out.filter((i) => i.severity === severityFilter);
    out.sort((a, b) =>
      (SEV_ORDER[a.severity] ?? 5) - (SEV_ORDER[b.severity] ?? 5) ||
      String(a.title).localeCompare(String(b.title))
    );
    return out;
  }, [issues, categoryFilter, severityFilter]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((i) => selected[i.id]);

  const toggleAll = () => {
    const next = { ...selected };
    if (allVisibleSelected) {
      filtered.forEach((i) => { delete next[i.id]; });
    } else {
      filtered.forEach((i) => { next[i.id] = true; });
    }
    onSelectionChange(next);
  };

  if (!issues || !issues.length) {
    return (
      <div className="p-8 border border-[#E5E5E0] rounded-2xl text-center bg-white" data-testid="compliance-issues-empty">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#166534]" />
        <p className="mt-3 text-slate-700 font-medium">No issues detected — full compliance.</p>
        <p className="mt-1 text-sm text-slate-500">Every check passed on the latest scan.</p>
      </div>
    );
  }

  return (
    <div className="border border-[#E5E5E0] rounded-2xl overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 mr-1 cursor-pointer">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAll}
            data-testid="compliance-issues-select-all"
          />
          <span className="text-xs text-slate-500">Select all visible</span>
        </label>
        <Filter className="w-4 h-4 text-[#1E3A8A]" />
        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Severity</div>
        {["", "critical", "high", "medium", "low", "info"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => onSeverityFilterChange(s)}
            className={`px-3 py-1 text-xs rounded-full border ${
              severityFilter === s
                ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                : "bg-white text-slate-700 border-[#E5E5E0] hover:border-[#94A3B8]"
            }`}
            data-testid={`compliance-severity-filter-${s || "all"}`}
          >
            {s ? SEVERITY_TONE[s]?.label : "All"}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500">
          {filtered.length} of {issues.length}
        </div>
      </div>
      <div className="divide-y divide-[#E5E5E0]" data-testid="compliance-issues-list">
        {filtered.map((it) => {
          const tone = SEVERITY_TONE[it.severity] || SEVERITY_TONE.info;
          const isOpen = !!expanded[it.id];
          const safety = SAFETY_TONE[it.safety_level] || null;
          const isChecked = !!selected[it.id];
          return (
            <div key={it.id} className="p-4" data-testid={`compliance-issue-${it.id}`}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={isChecked}
                  onChange={(e) => {
                    const next = { ...selected };
                    if (e.target.checked) next[it.id] = true; else delete next[it.id];
                    onSelectionChange(next);
                  }}
                  data-testid={`compliance-issue-select-${it.id}`}
                  aria-label="Select issue"
                />
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [it.id]: !prev[it.id] }))}
                  className="flex-1 flex items-start justify-between gap-4 text-left min-w-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold border ${tone.bg} ${tone.text} ${tone.border}`}
                      >
                        {tone.label}
                      </span>
                      {safety && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${safety.bg} ${safety.text} inline-flex items-center gap-1`}
                          data-testid={`compliance-issue-safety-${it.id}`}
                        >
                          <safety.Icon className="w-3 h-3" />
                          {safety.label}
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                        {it.category}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.12em] text-[#1E3A8A]">
                        {it.affected_module}
                      </span>
                    </div>
                    <div className="mt-1.5 text-sm font-semibold text-slate-900">{it.title}</div>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-slate-400 mt-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400 mt-1" />
                  )}
                </button>
                {it.can_auto_fix && it.safety_level === "safe_auto" && (
                  <button
                    type="button"
                    onClick={() => onRunFix(it, { approved: false })}
                    className="px-3 py-1.5 rounded-lg bg-[#166534] text-white text-xs font-medium hover:bg-[#0f4a24] flex items-center gap-1.5"
                    data-testid={`compliance-issue-fix-${it.id}`}
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Fix now
                  </button>
                )}
                {it.can_auto_fix && it.safety_level === "approval_required" && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(
                        `This action affects user-visible data.\n\nAction: ${it.auto_fix_action}\n\nConfirm approval?`
                      )) onRunFix(it, { approved: true });
                    }}
                    className="px-3 py-1.5 rounded-lg border border-[#92400E] text-[#92400E] text-xs font-medium hover:bg-[#FEF3C7] flex items-center gap-1.5"
                    data-testid={`compliance-issue-approve-${it.id}`}
                  >
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Approve & fix
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onConvertToException?.(it)}
                  className="px-3 py-1.5 rounded-lg border border-[#1E3A8A] text-[#1E3A8A] text-xs font-medium hover:bg-[#EFF6FF] flex items-center gap-1.5"
                  data-testid={`compliance-issue-convert-${it.id}`}
                  title="Convert this issue into a tracked Exception for owner assignment + SLA."
                >
                  <AlertOctagon className="w-3.5 h-3.5" />
                  To exception
                </button>
              </div>
              {isOpen && (
                <div className="mt-3 ml-6 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Description</div>
                    <div className="mt-1 text-sm text-slate-700">{it.description}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Remediation</div>
                    <div className="mt-1 text-sm text-slate-700">{it.remediation}</div>
                    {it.auto_fix_action && (
                      <div className="mt-2 text-[10px] uppercase tracking-[0.12em] text-slate-500">
                        Action key: <span className="font-mono text-[11px] text-slate-700">{it.auto_fix_action}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Settings drawer -------------------------------------
function SettingsDrawer({ open, onClose, settings, onSave, onSendTest }) {
  const [minScore, setMinScore] = useState(settings?.min_score ?? 90);
  const [alertOn, setAlertOn] = useState(settings?.alert_email_below_target ?? true);
  const [cooldown, setCooldown] = useState(settings?.alert_cooldown_hours ?? 6);
  const [recipients, setRecipients] = useState((settings?.notify_recipients || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setMinScore(settings?.min_score ?? 90);
    setAlertOn(settings?.alert_email_below_target ?? true);
    setCooldown(settings?.alert_cooldown_hours ?? 6);
    setRecipients((settings?.notify_recipients || []).join(", "));
  }, [settings, open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        min_score: Number(minScore),
        alert_email_below_target: !!alertOn,
        alert_cooldown_hours: Number(cooldown),
        notify_recipients: recipients.split(",").map((e) => e.trim()).filter(Boolean),
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try { await onSendTest(); } finally { setTesting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="compliance-settings-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-center gap-3">
          <SettingsIcon className="w-5 h-5 text-[#1E3A8A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">Compliance</div>
            <h2 className="text-lg font-semibold text-slate-900">Alert & Threshold Settings</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-slate-500 hover:text-slate-800"
            data-testid="compliance-settings-close"
          >
            ×
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">Minimum acceptable score</label>
            <input
              type="number"
              min={0}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
              data-testid="compliance-settings-min-score"
            />
            <p className="mt-1 text-xs text-slate-500">
              Alerts fire when the score falls below this value.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={alertOn}
              onChange={(e) => setAlertOn(e.target.checked)}
              className="mt-1"
              data-testid="compliance-settings-alert-toggle"
            />
            <span>
              <div className="text-sm font-medium text-slate-900">Email me when score drops below target</div>
              <div className="text-xs text-slate-500 mt-0.5">
                One digest per scan (not one per issue).
              </div>
            </span>
          </label>

          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">Alert cooldown (hours)</label>
            <input
              type="number"
              min={0}
              max={168}
              value={cooldown}
              onChange={(e) => setCooldown(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
              data-testid="compliance-settings-cooldown"
            />
            <p className="mt-1 text-xs text-slate-500">
              Prevents alert storms if the score stays low across consecutive scans.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">Notification recipients</label>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={3}
              placeholder="admin@harborwell.care, security@harborwell.care"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg font-mono text-xs"
              data-testid="compliance-settings-recipients"
            />
            <p className="mt-1 text-xs text-slate-500">
              Comma-separated. Leave empty to fall back to all staff with `is_it_support=true`.
            </p>
          </div>

          <div className="pt-4 border-t border-[#E5E5E0] flex flex-wrap gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50"
              data-testid="compliance-settings-save"
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
            <button
              type="button"
              onClick={sendTest}
              disabled={testing}
              className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
              data-testid="compliance-settings-test-alert"
            >
              <Mail className="w-4 h-4 inline mr-1.5" />
              {testing ? "Sending…" : "Send test alert"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Score Trend Chart (Wave B4) --------------------------
function ScoreTrendChart({ series, minScore }) {
  if (!series || series.length === 0) return null;
  const data = series.map((s) => ({
    ts: new Date(s.started_at).getTime(),
    label: new Date(s.started_at).toLocaleDateString(undefined, {
      month: "short", day: "numeric",
    }),
    score: Number(s.overall_score) || 0,
  }));
  return (
    <div className="p-4 rounded-2xl bg-white border border-[#E5E5E0]" data-testid="compliance-score-trend">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-[#1E3A8A]" />
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
          Compliance score trend
        </div>
        <span className="text-xs text-slate-500 ml-auto">
          {series.length} scan{series.length === 1 ? "" : "s"} · last 90 days
        </span>
      </div>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 12, bottom: 4, left: -18 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={20} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={(v) => [`${Number(v).toFixed(1)}`, "Score"]}
              contentStyle={{ borderRadius: 10, border: "1px solid #E5E5E0", fontSize: 12 }}
            />
            {minScore != null && (
              <ReferenceLine y={minScore} stroke="#991B1B" strokeDasharray="3 3"
                              label={{ value: `Target ≥ ${minScore}`, fill: "#991B1B", fontSize: 10 }} />
            )}
            <Line type="monotone" dataKey="score" stroke="#1E3A8A" strokeWidth={2}
                   dot={{ r: 2, fill: "#1E3A8A" }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------- Saved filters (Wave B4) ------------------------------
const SAVED_FILTERS_KEY = "harborwell:compliance:saved_filters";

function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(SAVED_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function persistSavedFilters(list) {
  try { localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(list)); } catch { /* localStorage disabled — ignore */ }
}

function SavedFiltersBar({ current, onApply }) {
  const [filters, setFilters] = useState(loadSavedFilters);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");

  const save = () => {
    if (!newName.trim()) return;
    const next = [...filters.filter((f) => f.name !== newName.trim()),
                   { name: newName.trim(), ...current }];
    setFilters(next); persistSavedFilters(next);
    setNewName(""); setShowNew(false);
    toast.success(`Filter "${newName.trim()}" saved.`);
  };
  const remove = (name) => {
    const next = filters.filter((f) => f.name !== name);
    setFilters(next); persistSavedFilters(next);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="compliance-saved-filters-bar">
      <Bookmark className="w-3.5 h-3.5 text-[#1E3A8A]" />
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Saved filters</div>
      {filters.length === 0 && (
        <span className="text-xs text-slate-400">none yet</span>
      )}
      {filters.map((f) => (
        <button
          key={f.name}
          type="button"
          onClick={() => onApply(f)}
          className="group px-3 py-1 rounded-full border border-[#E5E5E0] text-xs bg-white hover:border-[#94A3B8] flex items-center gap-1.5"
          data-testid={`compliance-saved-filter-${f.name.toLowerCase().replace(/\W+/g, "-")}`}
        >
          {f.name}
          <span
            className="opacity-0 group-hover:opacity-100 text-[#991B1B]"
            onClick={(e) => { e.stopPropagation(); remove(f.name); }}
            role="button"
            aria-label="delete filter"
          >×</span>
        </button>
      ))}
      {showNew ? (
        <div className="inline-flex items-center gap-1.5">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Filter name"
            className="px-2 py-1 border border-[#E5E5E0] rounded-lg text-xs"
            data-testid="compliance-saved-filter-name"
            autoFocus
          />
          <button type="button" onClick={save}
                  className="px-2 py-1 rounded-lg bg-[#1E3A8A] text-white text-xs"
                  data-testid="compliance-saved-filter-save">Save</button>
          <button type="button" onClick={() => setShowNew(false)}
                  className="text-xs text-slate-500">Cancel</button>
        </div>
      ) : (
        <button type="button" onClick={() => setShowNew(true)}
                className="text-xs text-[#1E3A8A] underline"
                data-testid="compliance-saved-filter-new">
          <Save className="w-3 h-3 inline mr-1" />
          Save current
        </button>
      )}
    </div>
  );
}

// ---------------- Main page -------------------------------------------
export default function AdminCompliance() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [overview, setOverview] = useState(null);
  const [historySeries, setHistorySeries] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [severityFilter, setSeverityFilter] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState({});
  const [batchRunning, setBatchRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/compliance/overview");
      setOverview(data);
      setAccessDenied(false);
      // Fire-and-forget history — non-blocking
      api.get("/admin/compliance/history?days=90")
         .then((h) => setHistorySeries(h.data?.series || []))
         .catch(() => setHistorySeries([]));
    } catch (e) {
      if (e?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/compliance/scans", { notify: true });
      toast.success(`Scan complete · Score ${data.overall_score.toFixed(1)} (${data.grade})`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.patch("/admin/compliance/settings", patch);
      setOverview((prev) => (prev ? { ...prev, settings: data } : prev));
      toast.success("Settings saved.");
      setSettingsOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendTestAlert = async () => {
    try {
      const { data } = await api.post("/admin/compliance/test-alert");
      if (data.sent) {
        toast.success(`TEST alert sent to ${(data.to || []).length} recipient(s).`);
      } else {
        toast.error(data.reason || "Test alert failed.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runFix = async (issue, { approved }) => {
    try {
      const scanId = overview?.latest?.id;
      const { data } = await api.post(`/admin/compliance/issues/${issue.id}/auto-fix`, {
        scan_id: scanId,
        approved,
      });
      if (data.ok) {
        toast.success(`Fixed · ${issue.title}`);
      } else if (data.skipped) {
        toast.warning(data.reason || "Fix skipped.");
      } else {
        toast.error(data.reason || "Fix failed.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runAllSafe = async () => {
    const safeCount = (overview?.latest?.issues || []).filter(
      (i) => i.safety_level === "safe_auto" && i.can_auto_fix
    ).length;
    if (safeCount === 0) {
      toast.info("No safe auto-fix candidates in the latest scan.");
      return;
    }
    if (!window.confirm(
      `About to auto-execute ${safeCount} SAFE fix${safeCount === 1 ? "" : "es"}.\n\n` +
      `Only Safe Auto-Fix actions run — approval-required and manual-only ` +
      `actions are never auto-executed.\n\nContinue?`
    )) return;
    setBatchRunning(true);
    try {
      const { data } = await api.post("/admin/compliance/auto-fix-all-safe", {
        scan_id: overview?.latest?.id,
      });
      const msg =
        `Executed ${data.executed}/${data.total_candidates} safe fixes` +
        (data.failed ? ` · ${data.failed} failed` : "") +
        (data.new_scan
          ? ` · New score ${data.new_scan.overall_score.toFixed(1)} (${data.new_scan.grade})`
          : "");
      (data.failed ? toast.warning : toast.success)(msg);
      setSelectedIssues({});
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBatchRunning(false);
    }
  };

  const convertToException = async (issue) => {
    try {
      const { data } = await api.post("/compliance/exceptions/from-issue", {
        scan_id: overview?.latest?.id,
        issue_id: issue.id,
      });
      toast.success(`Exception ${data.code} created for "${issue.title}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const latest = overview?.latest;
  const settings = overview?.settings;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <div
          className="max-w-md w-full p-8 rounded-3xl bg-white border border-[#E5E5E0] text-center shadow-sm"
          data-testid="compliance-access-denied"
        >
          <div className="mx-auto w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-[#991B1B]" />
          </div>
          <h1 className="mt-4 text-lg font-semibold text-slate-900">Compliance Center is restricted</h1>
          <p className="mt-2 text-sm text-slate-600">
            Your role doesn&apos;t have the <span className="font-mono text-xs">compliance.view</span> permission.
            Ask a HarborWell administrator to grant it under Roles &amp; permissions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        {/* Header + score */}
        <div className="p-6 md:p-8 rounded-3xl bg-white border border-[#E5E5E0] shadow-sm">
          <ScoreDial score={latest?.overall_score} grade={latest?.grade} />
          <div className="mt-6 flex flex-wrap items-center gap-3 pt-6 border-t border-[#E5E5E0]">
            <button
              type="button"
              onClick={runScan}
              disabled={running}
              className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="compliance-run-scan"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {running ? "Running scan…" : "Run scan now"}
            </button>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm font-medium bg-white hover:bg-slate-50 flex items-center gap-2"
              data-testid="compliance-open-settings"
            >
              <SettingsIcon className="w-4 h-4" />
              Settings & Alerts
            </button>
            <button
              type="button"
              onClick={runAllSafe}
              disabled={batchRunning || running}
              className="px-4 py-2 rounded-lg bg-[#166534] text-white text-sm font-medium hover:bg-[#0f4a24] flex items-center gap-2 disabled:opacity-50"
              data-testid="compliance-fix-all-safe"
              title="Executes every issue tagged 'Safe auto-fix'. Approval-required and manual-only actions are never auto-executed."
            >
              {batchRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {batchRunning ? "Fixing…" : "Fix all safe issues"}
            </button>
            <div className="ml-auto text-xs text-slate-500 flex items-center gap-2" data-testid="compliance-last-scan">
              <Bell className="w-3.5 h-3.5" />
              Last scan{" "}
              {latest?.started_at ? formatRelative(latest.started_at) : "—"} ·{" "}
              triggered_by <span className="font-mono text-[10px]">{latest?.triggered_by || "—"}</span>
              {settings?.min_score != null && (
                <span className="ml-2 pl-2 border-l border-[#E5E5E0]">
                  Target ≥ {settings.min_score}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Score Trend Chart (Wave B4) */}
        {historySeries.length > 1 && (
          <div className="mt-6">
            <ScoreTrendChart series={historySeries} minScore={settings?.min_score} />
          </div>
        )}

        {/* Category grid */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-slate-900">Categories</h2>
            <SavedFiltersBar
              current={{ category: categoryFilter, severity: severityFilter }}
              onApply={(f) => {
                setCategoryFilter(f.category || null);
                setSeverityFilter(f.severity || "");
              }}
            />
            {categoryFilter && (
              <button
                type="button"
                onClick={() => setCategoryFilter(null)}
                className="text-xs text-[#1E3A8A] underline"
                data-testid="compliance-clear-category-filter"
              >
                Clear filter · {categoryFilter}
              </button>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {(latest?.categories || []).map((c) => (
              <CategoryCard
                key={c.key}
                cat={c}
                onClick={() =>
                  setCategoryFilter((prev) => (prev === c.key ? null : c.key))
                }
                isSelected={categoryFilter === c.key}
              />
            ))}
          </div>
        </div>

        {/* Issues */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900">
              Issues {categoryFilter ? `· ${categoryFilter}` : ""}
            </h2>
          </div>
          <IssuesTable
            issues={latest?.issues || []}
            categoryFilter={categoryFilter}
            severityFilter={severityFilter}
            onSeverityFilterChange={setSeverityFilter}
            selected={selectedIssues}
            onSelectionChange={setSelectedIssues}
            onRunFix={runFix}
            onConvertToException={convertToException}
            scanId={latest?.id}
          />
        </div>
      </div>

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSave={saveSettings}
        onSendTest={sendTestAlert}
      />
    </div>
  );
}
